<script setup>
import { useStore } from "vuex";
import { computed, ref, nextTick, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router"
import { errorNotify, successNotify } from "@/mixins";
import { useTheme } from "@/composables/useTheme";
import wordmarkDark from "@/assets/brand/lockerbot-wordmark-dark.svg";
import wordmarkWhite from "@/assets/brand/lockerbot-wordmark-white.svg";

const store = useStore()
const router = useRouter()
const error = computed(() => store.getters['auth/authError'])
const login = (val) => store.dispatch('auth/login', val)
const sendOTP = (val) => store.dispatch('auth/sendOTP', val)
const getCurrentUser = () => store.dispatch('user/getCurrentUser')

const currentStep = ref('email')
const email = ref('')
const emailTouched = ref(false)
const otp = ref(['', '', '', '', '', ''])
const codeError = ref('')
const isLoading = ref(false)

const otpRefs = Array.from({ length: 6 }, () => ref(null))

const { currentTheme } = useTheme()
const logoSource = computed(() => (currentTheme.value === 'dark' ? wordmarkWhite : wordmarkDark))

const emailOk = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()))

// resend-code cooldown timer
const RESEND_COOLDOWN = 30
const cooldown = ref(0)
let cooldownTimer = null
function startCooldown() {
  cooldown.value = RESEND_COOLDOWN
  clearInterval(cooldownTimer)
  cooldownTimer = setInterval(() => {
    cooldown.value -= 1
    if (cooldown.value <= 0) clearInterval(cooldownTimer)
  }, 1000)
}
onBeforeUnmount(() => clearInterval(cooldownTimer))

function requestOTP() {
  emailTouched.value = true
  if (!emailOk.value || isLoading.value) return
  isLoading.value = true
  sendOTP({ email: email.value.trim() })
    .then(() => {
      if (error.value) {
        if (error.value.otp_code && error.value.otp_code[0] && error.value.otp_code[0].includes('already sent')) {
          toOtpStep()
          successNotify('Код уже отправлен на ваш email')
        } else {
          errorNotify(error.value)
        }
      } else {
        toOtpStep()
        successNotify('Код отправлен на ваш email')
      }
    })
    .catch(() => {
      if (error.value) errorNotify(error.value)
    })
    .finally(() => {
      isLoading.value = false
    })
}

function toOtpStep() {
  currentStep.value = 'otp'
  otp.value = ['', '', '', '', '', '']
  codeError.value = ''
  startCooldown()
  nextTick(() => otpRefs[0].value && otpRefs[0].value.focus())
}

function resendOTP() {
  if (cooldown.value > 0 || isLoading.value) return
  otp.value = ['', '', '', '', '', '']
  codeError.value = ''
  isLoading.value = true
  sendOTP({ email: email.value.trim() })
    .then(() => {
      if (error.value) {
        errorNotify(error.value)
      } else {
        successNotify('Код отправлен повторно')
        startCooldown()
      }
    })
    .finally(() => {
      isLoading.value = false
    })
}

function doLogin() {
  const otpValue = otp.value.join('')
  if (otpValue.length !== 6 || otp.value.some(d => d === '')) {
    codeError.value = 'Введите все 6 цифр.'
    return
  }
  if (isLoading.value) return
  isLoading.value = true
  codeError.value = ''
  login({ email: email.value.trim(), password: otpValue })
    .then(() => {
      if (error.value) {
        codeError.value = 'Неверный код. Попробуйте ещё раз.'
        errorNotify(error.value)
      } else {
        getCurrentUser()
        router.push({ name: 'dashboard' })
      }
    })
    .catch(() => {
      if (error.value) {
        codeError.value = 'Неверный код. Попробуйте ещё раз.'
        errorNotify(error.value)
      }
    })
    .finally(() => {
      isLoading.value = false
    })
}

function backToEmail() {
  currentStep.value = 'email'
  otp.value = ['', '', '', '', '', '']
  codeError.value = ''
}

function handleOTPInput(e, idx) {
  const val = e.target.value.replace(/\D/g, '')
  codeError.value = ''
  if (!val) {
    otp.value[idx] = ''
    return
  }
  otp.value[idx] = val[val.length - 1]
  if (idx < 5) {
    nextTick(() => otpRefs[idx + 1].value && otpRefs[idx + 1].value.focus())
  }
  if (otp.value.every(d => d !== '')) {
    nextTick(() => doLogin())
  }
}

function handleOTPKeydown(e, idx) {
  if (e.key === 'Backspace' && !otp.value[idx] && idx > 0) {
    nextTick(() => otpRefs[idx - 1].value && otpRefs[idx - 1].value.focus())
  }
}

function handleOTPPaste(e) {
  const paste = ((e.clipboardData || window.clipboardData).getData('text') || '').replace(/\D/g, '').slice(0, 6)
  if (!paste) return
  e.preventDefault()
  for (let i = 0; i < 6; i++) {
    otp.value[i] = paste[i] || ''
  }
  const idx = Math.min(paste.length, 5)
  nextTick(() => {
    otpRefs[idx].value && otpRefs[idx].value.focus()
    if (paste.length === 6) doLogin()
  })
}
</script>

<template>
  <div class="authwrap">
    <div class="authcard">
      <img class="authlogo" :src="logoSource" alt="Lockerbot">
      <div class="authtag">Network Admin</div>

      <template v-if="currentStep === 'email'">
        <h1 class="authtitle">Вход в систему</h1>
        <p class="authsub">Введите рабочий email — мы отправим одноразовый код.</p>
        <label class="field authfield">
          <span class="field__label">Email</span>
          <input
            class="field__input"
            :class="{ 'field__input--error': emailTouched && !emailOk }"
            type="email"
            placeholder="you@company.com"
            autofocus
            v-model="email"
            :disabled="isLoading"
            @keydown.enter="requestOTP">
          <span v-if="emailTouched && !emailOk" class="field__error">Введите корректный email.</span>
        </label>
        <button class="btn-primary authbtn" type="button" :disabled="isLoading" @click="requestOTP">
          <span class="mdi" :class="isLoading ? 'mdi-loading mdi-spin' : 'mdi-email-fast-outline'"></span>
          {{ isLoading ? 'Отправка…' : 'Отправить код' }}
        </button>
      </template>

      <template v-else>
        <h1 class="authtitle">Введите код</h1>
        <p class="authsub">Мы отправили 6-значный код на <b>{{ email }}</b>.</p>
        <div class="otp-row" @paste="handleOTPPaste">
          <input
            v-for="(digit, idx) in otp"
            :key="idx"
            class="otp-box"
            :class="{ 'is-filled': digit }"
            type="text"
            inputmode="numeric"
            maxlength="1"
            autocomplete="one-time-code"
            v-model="otp[idx]"
            :ref="el => otpRefs[idx].value = el"
            :disabled="isLoading"
            @input="e => handleOTPInput(e, idx)"
            @keydown="e => handleOTPKeydown(e, idx)">
        </div>
        <div v-if="codeError" class="field__error otp-error">{{ codeError }}</div>
        <button class="btn-primary authbtn" type="button" :disabled="isLoading" @click="doLogin">
          <span class="mdi" :class="isLoading ? 'mdi-loading mdi-spin' : 'mdi-shield-check-outline'"></span>
          {{ isLoading ? 'Проверка…' : 'Подтвердить и войти' }}
        </button>
        <div class="authrow">
          <button class="linklike" type="button" @click="backToEmail">
            <span class="mdi mdi-arrow-left"></span>
            Другой email
          </button>
          <span class="authrow__spacer"></span>
          <span v-if="cooldown > 0" class="authresend is-disabled">
            Повтор через 0:{{ String(cooldown).padStart(2, '0') }}
          </span>
          <button v-else class="linklike" type="button" @click="resendOTP">Отправить код повторно</button>
        </div>
      </template>

      <div class="authfoot">© 2026 Lockerbot · admin@lockerbot.io</div>
    </div>
  </div>
</template>
